import { useQuery } from '@tanstack/react-query'
import { AppWindow } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { PageHeader } from '@/components/ui/page-header'
import { apiClient } from '@/lib/api-client'
import { accountStatusColor, accountStatusLabel, billingLabel, nextRenewalDate } from '@/lib/software-format'
import { formatDateShort } from '@shared/utils'

export function EmployeeSoftwarePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-software'],
    queryFn: async () => (await apiClient.get('/software/mine')).data,
  })

  const accounts = data?.accounts || []

  return (
    <div className="space-y-4">
      <PageHeader title="My software" />

      <div className="data-panel">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <AppWindow className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-sm font-medium">No software accounts yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Paid accounts assigned to you will show up here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Software</th>
                  <th>Account</th>
                  <th>Plan</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account: any) => {
                  const nextRenewal = nextRenewalDate(account)
                  return (
                    <tr key={account.id}>
                      <td>
                        <p className="text-sm font-medium">{account.software?.name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{account.software?.vendor}</p>
                      </td>
                      <td data-label="Account">
                        <p className="text-sm font-medium">{account.accountName}</p>
                        <p className="truncate text-xs text-muted-foreground">{account.email}</p>
                      </td>
                      <td data-label="Plan">
                        <p className="text-sm">{account.plan || '—'}</p>
                        <p className="text-xs text-muted-foreground">{billingLabel(account)}</p>
                        {account.billingCycle !== 'one-time' && nextRenewal && (
                          <p className="text-xs text-muted-foreground">Next renewal {formatDateShort(nextRenewal)}</p>
                        )}
                      </td>
                      <td data-label="Status">
                        <Badge className={accountStatusColor(account.status)}>{accountStatusLabel(account.status)}</Badge>
                        {account.statusReason && (
                          <p className="mt-1 max-w-[18rem] text-xs text-muted-foreground">{account.statusReason}</p>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
